/* global debounce, trapFocus */

if (!customElements.get('cart-items')) {
  class CartItems extends HTMLElement {
    constructor() {
      super();
      this.restoreCartNotice();
      if (this.dataset.empty === 'false') {
        this.init();
        this.setClientCheckoutState(false);
        queueMicrotask(() => this.reconcileCart());
      }
    }

    init() {
      if (this.initialized) return;
      this.initialized = true;

      this.fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };

      this.cartDrawer = document.getElementById('cart-drawer');
      this.itemStatus = document.getElementById('cart-line-item-status');
      this.syncStateFromDom();

      this.addEventListener('click', this.handleClick.bind(this));
      const debouncedChange = debounce(this.handleChange.bind(this));
      this.addEventListener('change', (evt) => {
        if (!evt.target.matches('.qty-input__input')) return;
        // Lock immediately, including the debounce interval and any older validation.
        this.cartUpdatePending = true;
        this.setClientCheckoutState(false);
        debouncedChange(evt);
      });
    }

    /**
     * Handles 'click' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      const removeButton = evt.target.closest('.js-remove-item');
      if (!removeButton || !this.contains(removeButton)) return;
      evt.preventDefault();
      this.updateQuantity(removeButton.dataset.index, 0);
    }

    /**
     * Handles 'change' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
      this.updateQuantity(evt.target.dataset.index, evt.target.value, document.activeElement.name);
    }

    /**
     * Updates the quantity of a line item.
     * @param {number} line - Line item index.
     * @param {number} quantity - Quantity to set.
     * @param {string} name - Active element name.
     */
    async updateQuantity(line, quantity, name) {
      const cartDrawerContent = this.cartDrawer ? this.cartDrawer.querySelector('.drawer__content') : null;
      const cartDrawerContentScroll = cartDrawerContent ? cartDrawerContent.scrollTop : 0;
      const cartDrawerScroll = this.cartDrawer ? this.cartDrawer.scrollTop : 0;

      this.enableLoading(line);

      // clear all errors except this line's (which will be refreshed after this update)
      const lineErrorsId = `line-item-error-${line}`;
      const lineErrors = document.getElementById(lineErrorsId);
      document.querySelectorAll(`.cart-errors, .cart-item__error:not([id="${lineErrorsId}"])`).forEach((el) => {
        el.innerHTML = '';
        el.hidden = true;
      });

      const sections = this.getSectionsToRender().map((section) => section.section);
      this.fetchRequestOpts.body = JSON.stringify({
        line,
        quantity,
        sections: [...new Set(sections)],
        sections_url: window.location.pathname
      });

      try {
        const lineItem = document.getElementById(`cart-item-${line}`);
        const variantId = Number(lineItem?.dataset.variantId);
        const oldTotalQuantity = this.currentTotalItemCount;
        const response = await fetch(theme.routes.cartChange, this.fetchRequestOpts);
        const data = await response.json();

        if (!response.ok) throw new Error(data.errors || response.status);

        const customerItems = data.items.filter((item) => !CartItems.isOrderFeeLine(item));
        const newTotalQuantity = customerItems.reduce((total, item) => total + item.quantity, 0);

        if (this.cartDrawer) {
          cartDrawerContent.classList.toggle('drawer__content--flex', newTotalQuantity === 0);

          if (newTotalQuantity === 0) {
            const recommendations = this.cartDrawer.querySelector('product-recommendations');
            if (recommendations) recommendations.remove();
          }
        } else if (newTotalQuantity === 0) {
          // We're on the Cart page
          const cartTitle = this.closest('.cc-main-cart').querySelector('.js-cart-title');
          if (cartTitle) cartTitle.style.textAlign = 'center';

          const cartSummaryCss = document.getElementById('cart-summary-css');
          if (cartSummaryCss) cartSummaryCss.remove();

          const cartContent = document.getElementById('cart-summary-and-blocks');
          if (cartContent) cartContent.hidden = true;
        }

        this.getSectionsToRender().forEach((section) => {
          const sectionEl = document.getElementById(section.id);
          if (!sectionEl) return;

          const { selector } = section;
          const el = sectionEl.querySelector(selector) || sectionEl;
          CartItems.replaceElementContents(el, data.sections[section.section], selector);
        });

        if (this.cartDrawer && newTotalQuantity === 0) {
          cartDrawerContent.classList.add('grow', 'flex', 'items-center');

          if (this.cartDrawer.querySelector('promoted-products')) {
            this.cartDrawer
              .querySelector('.drawer__content')
              .classList.toggle('drawer__empty-with-promotions', newTotalQuantity === 0);
          }
        }

        this.syncStateFromDom();
        const firstValidCartItem = this.querySelector('.cart-item[data-cart-item-valid="true"]');
        this.updateRecommendations(firstValidCartItem ? firstValidCartItem.dataset.productId : null);
        this.updateLiveRegions();
        this.setFocus(line, newTotalQuantity, name);
        this.currentTotalItemCount = Number(this.dataset.validItemCount || 0);

        // Fire the on:line-item:change event if the line item quantity has changed
        if (oldTotalQuantity !== newTotalQuantity) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              variantId,
              oldQuantity: this.currentQuantities[variantId],
              newQuantity: Number(quantity)
            }
          }));
        }

        this.currentQuantities[variantId] = Number(quantity);

        if (lineErrors) {
          lineErrors.innerHTML = '';
          lineErrors.hidden = true;
        }

        this.cartUpdatePending = false;
        await this.reconcileCart();
      } catch (error) {
        if (lineErrors) {
          if (/^[0-9]+$/.test(error.message)) {
            lineErrors.textContent = theme.strings.cartError;
          } else {
            lineErrors.textContent = error.message;
          }
          lineErrors.hidden = false;
        }
        console.log(error); // eslint-disable-line

        this.querySelectorAll('.cart-item__loader').forEach((loader) => {
          loader.hidden = true;
        });

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: error.message
          }
        }));

        const input = document.getElementById(`quantity-${line}`);
        if (input) {
          input.value = input.dataset.initialValue;
          input.closest('quantity-input').currentQty = input.dataset.initialValue;
        }
      } finally {
        this.cartUpdatePending = false;
        this.classList.remove('pointer-events-none');

        // Attempt to maintain the same scroll position in the cart drawer
        if (cartDrawerContent) {
          requestAnimationFrame(() => { cartDrawerContent.scrollTop = cartDrawerContentScroll; });
          setTimeout(() => { cartDrawerContent.scrollTop = cartDrawerContentScroll; }, 0);
          requestAnimationFrame(() => { this.cartDrawer.scrollTop = cartDrawerScroll; });
          setTimeout(() => { this.cartDrawer.scrollTop = cartDrawerScroll; }, 0);
        }
      }
    }

    /**
     * Refreshes the cart by rerendering its sections and updating its product recommendations.
     */
    async refresh() {
      if (!this.initialized) this.init();
      this.setClientCheckoutState(false);
      const errors = document.getElementById('cart-errors');
      try {
        const sections = this.getSectionsToRender().map((section) => section.section);
        const response = await fetch(`?sections=${[...new Set(sections)]}`);

        // The status is 400, refresh the whole cart and stop
        if (response.status === 400 && this.cartDrawer) {
          this.cartDrawer.refresh(true);
          return;
        }

        const data = await response.json();

        if (!response.ok) throw new Error(response.status);

        this.getSectionsToRender().forEach((section) => {
          const sectionEl = document.getElementById(section.id);
          if (!sectionEl) return;

          const el = sectionEl.querySelector(section.selector) || sectionEl;
          CartItems.replaceElementContents(el, data[section.section], section.selector);
        });

        this.syncStateFromDom();
        const firstCartItem = this.querySelector('.cart-item[data-cart-item-valid="true"]');
        this.updateRecommendations(firstCartItem ? firstCartItem.dataset.productId : null);

        errors.innerHTML = '';
        errors.hidden = true;

        await this.reconcileCart();
      } catch (error) {
        errors.textContent = theme.strings.cartError;
        errors.hidden = false;
        console.log(error); // eslint-disable-line

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: error.message
          }
        }));
      }
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      this.cartDrawer = this.cartDrawer || this.closest('cart-drawer') || document.getElementById('cart-drawer');
      let sections = [
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        },
        {
          id: 'free-shipping-notice',
          section: 'free-shipping-notice',
          selector: '.free-shipping-notice'
        }
      ];

      if (this.cartDrawer) {
        const cartDrawerId = this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '');
        sections = [
          ...sections,
          {
            id: 'cart-items',
            section: cartDrawerId,
            selector: 'cart-items'
          },
          {
            id: 'cart-promoted-products',
            section: cartDrawerId,
            selector: '#cart-promoted-products'
          },
          {
            id: 'cart-drawer',
            section: cartDrawerId,
            selector: '.cart-drawer__summary'
          },
          {
            id: 'cart-drawer-media-promotion',
            section: cartDrawerId,
            selector: '#cart-drawer-media-promotion'
          }
        ];
      } else {
        const cartSummary = document.getElementById('cart-summary');
        sections = [
          ...sections,
          {
            id: 'cart-items',
            section: this.dataset.section,
            selector: 'cart-items'
          },
          ...(cartSummary ? [{
            id: 'cart-summary',
            section: cartSummary.dataset.section,
            selector: '.cart__summary'
          }] : [])
        ];
      }

      return sections;
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element to get the innerHTML of.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    /**
     * Replaces an element's rendered contents and keeps cart state attributes in sync.
     * @param {Element} target - Existing section element.
     * @param {string} html - Section HTML.
     * @param {string} selector - Selector for the rendered element.
     */
    static replaceElementContents(target, html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;
      const source = tmpl.content.querySelector(selector);
      target.innerHTML = source ? source.innerHTML : '';

      if (source && target.matches('cart-items')) {
        ['empty', 'validItemCount', 'unavailableItemCount', 'feeLineCount', 'checkoutReady']
          .forEach((key) => {
            if (source.dataset[key] === undefined) {
              delete target.dataset[key];
            } else {
              target.dataset[key] = source.dataset[key];
            }
          });
      }
    }

    /**
     * Rebuilds client-side quantity state after a server-rendered section refresh.
     */
    syncStateFromDom() {
      this.currentTotalItemCount = Number(this.dataset.validItemCount || 0);
      this.currentQuantities = [];
      this.querySelectorAll('.cart-item[data-cart-item-valid="true"]').forEach((item) => {
        const quantityInput = item.querySelector('.qty-input__input');
        if (quantityInput) this.currentQuantities[item.dataset.variantId] = Number(quantityInput.value);
      });
    }

    /**
     * Keeps checkout fail-closed until current merchandise has been verified through
     * Shopify's public product JSON. Archived and Draft products can retain stale
     * `available` flags on an existing cart line, so Liquid alone is not sufficient.
     * @param {boolean} clientVerified - Whether every current merchandise line was verified.
     */
    setClientCheckoutState(clientVerified) {
      document.querySelectorAll('[data-ansably-checkout-control]').forEach((button) => {
        const terms = button.form ? button.form.querySelector('.js-cart-terms-checkbox') : null;
        const ready = clientVerified && !this.cartUpdatePending
          && button.dataset.serverReady === 'true' && (!terms || terms.checked);
        button.disabled = !ready;
        button.setAttribute('aria-disabled', String(!ready));
      });

      document.querySelectorAll('[data-ansably-additional-checkout]').forEach((buttons) => {
        buttons.hidden = !(clientVerified && !this.cartUpdatePending && this.dataset.checkoutReady === 'true');
      });
    }

    /**
     * Displays a cart notice saved immediately before a full-cart reload.
     */
    restoreCartNotice() {
      const notice = sessionStorage.getItem('ansably-cart-notice');
      if (!notice) return;
      this.cartNotice = notice;
      sessionStorage.removeItem('ansably-cart-notice');
      document.querySelectorAll('[data-client-validation-alert]').forEach((alert) => {
        alert.textContent = notice;
        alert.hidden = false;
      });
    }

    /**
     * Shows or clears the client-side availability notice.
     * @param {string} message - Notice text, or an empty string to hide it.
     */
    setClientValidationAlert(message) {
      const notice = message || this.cartNotice || '';
      this.querySelectorAll('[data-client-validation-alert]').forEach((alert) => {
        alert.textContent = notice;
        alert.hidden = !notice;
      });
    }

    /**
     * Verifies every merchandise line against the public product JSON endpoint.
     * A 404, missing variant, or unavailable variant is definitive; other request
     * failures remain unknown and keep checkout disabled without deleting the line.
     * @returns {Promise<{unavailableLines: Array<Element>, hasUnknown: boolean, validItemCount: number}>}
     */
    async validateMerchandiseAvailability() {
      const lines = [...this.querySelectorAll('.cart-item[data-cart-item-valid="true"]')];
      const results = await Promise.all(lines.map(async (line) => {
        const productUrl = line.dataset.productUrl;
        if (!productUrl) return { line, state: 'unknown' };

        try {
          const availabilityUrl = `${productUrl}.js?ansably_cart_check=${Date.now()}`;
          const response = await fetch(availabilityUrl, {
            headers: { Accept: 'application/json' },
            cache: 'no-store'
          });
          if (response.status === 404) return { line, state: 'unavailable' };
          if (!response.ok) return { line, state: 'unknown' };

          const product = await response.json();
          const variant = product.variants?.find((candidate) => Number(candidate.id) === Number(line.dataset.variantId));
          return { line, state: product.available && variant?.available ? 'available' : 'unavailable' };
        } catch (error) {
          return { line, state: 'unknown' };
        }
      }));

      const unavailableLines = results.filter((result) => result.state === 'unavailable').map((result) => result.line);
      const hasUnknown = results.some((result) => result.state === 'unknown');
      const validItemCount = results
        .filter((result) => result.state === 'available')
        .reduce((total, result) => {
          const input = result.line.querySelector('.qty-input__input');
          return total + Number(input?.value || 0);
        }, 0);

      return { unavailableLines, hasUnknown, validItemCount };
    }

    /**
     * Removes unavailable merchandise and the retired handling-fee product from existing carts.
     */
    async reconcileCart() {
      this.setClientCheckoutState(false);

      const updates = {};
      const retiredFeeLines = [...this.querySelectorAll('[data-order-fee="true"]')]
        .filter((line) => Number(line.dataset.productId) === 9722722222309);
      retiredFeeLines.forEach((line) => {
        if (line.dataset.lineKey) updates[line.dataset.lineKey] = 0;
      });
      const serverUnavailableLines = [...this.querySelectorAll('[data-unavailable-line-key]')];
      const availability = await this.validateMerchandiseAvailability();
      const unavailableLines = [...new Set([...serverUnavailableLines, ...availability.unavailableLines])];

      if (availability.hasUnknown && retiredFeeLines.length === 0) {
        this.setClientValidationAlert('Item availability could not be verified. Checkout is paused; please refresh and try again.');
        return;
      }

      (availability.hasUnknown ? [] : unavailableLines).forEach((line) => {
        const lineKey = line.dataset.unavailableLineKey || line.dataset.lineKey;
        if (lineKey) updates[lineKey] = 0;
      });

      if (Object.keys(updates).length === 0) {
        this.setClientValidationAlert('');
        this.setClientCheckoutState(true);
        return;
      }

      if (unavailableLines.length > 0) {
        this.setClientValidationAlert('One or more items are no longer available and are being removed from your cart. Checkout is paused until the cart is updated.');
      }

      if (!window.ansablyCartReconcilePromise) {
        window.ansablyCartReconcilePromise = fetch(theme.routes.cartUpdate, {
          ...this.fetchRequestOpts,
          body: JSON.stringify({ updates })
        }).then(async (response) => {
          const data = await response.json();
          if (!response.ok) throw new Error(data.errors || response.status);
          return data;
        }).finally(() => {
          window.ansablyCartReconcilePromise = null;
        });
      }

      try {
        await window.ansablyCartReconcilePromise;
        if (unavailableLines.length > 0) {
          sessionStorage.setItem('ansably-cart-notice', 'Unavailable items were removed. Your cart has been updated.');
        }

        const reloadCount = Number(sessionStorage.getItem('ansably-cart-reconcile-reloads') || 0);
        if (window.location.pathname === theme.routes.cart && reloadCount < 2) {
          sessionStorage.setItem('ansably-cart-reconcile-reloads', String(reloadCount + 1));
          window.location.reload();
        } else {
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        }
      } catch (error) {
        const alert = document.getElementById('cart-errors');
        if (alert) {
          alert.textContent = 'Your cart could not be updated. Please remove unavailable items before checkout.';
          alert.hidden = false;
        }
        console.log(error); // eslint-disable-line
      }
    }

    /**
     * Identifies the retired fee product independently of its discounted price.
     * @param {object} item - Cart API line item.
     * @returns {boolean}
     */
    static isOrderFeeLine(item) {
      return Number(item.product_id) === 9722722222309;
    }

    /**
     * Shows a loading icon over a line item.
     * @param {string} line - Line item index.
     */
    enableLoading(line) {
      this.cartUpdatePending = true;
      this.setClientCheckoutState(false);
      this.classList.add('pointer-events-none');

      const loader = this.querySelector(`#cart-item-${line} .cart-item__loader`);
      if (loader) loader.hidden = false;

      document.activeElement.blur();
      if (this.itemStatus) this.itemStatus.setAttribute('aria-hidden', 'false');
    }

    /**
     * Updates the cart recommendations.
     * @param {string} productId - The product id for which to find recommendations.
     */
    updateRecommendations(productId) {
      this.recommendations = this.recommendations || document.getElementById('cart-recommendations');
      if (!this.recommendations) return;

      if (productId) {
        this.recommendations.dataset.productId = productId;
        this.recommendations.init();
      } else {
        this.recommendations.innerHTML = '';
      }
    }

    /**
     * Updates the live regions.
     */
    updateLiveRegions() {
      this.itemStatus.setAttribute('aria-hidden', 'true');

      const cartStatus = document.getElementById('cart-live-region-text');
      cartStatus.setAttribute('aria-hidden', 'false');

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Traps focus in the relevant container or focuses the active element.
     * @param {number} line - Line item index.
     * @param {number} itemCount - Item count.
     * @param {string} name - Active element name.
     */
    setFocus(line, itemCount, name) {
      const lineItem = document.getElementById(`cart-item-${line}`);
      let activeEl;

      if (lineItem) {
        activeEl = lineItem.querySelector(`[name="${name}"]`);
      }

      if (this.cartDrawer) {
        if (lineItem && activeEl) {
          trapFocus(this.cartDrawer, activeEl);
        } else if (itemCount === 0) {
          trapFocus(
            this.cartDrawer.querySelector('.js-cart-empty'),
            this.cartDrawer.querySelector('a')
          );
        } else if (this.cartDrawer.querySelector('.cart-item:not(.cart-item--order-fee)')) {
          trapFocus(this.cartDrawer, this.cartDrawer.querySelector('.js-item-name'));
        }
      } else if (lineItem && activeEl) {
        activeEl.focus();
      }
    }
  }

  customElements.define('cart-items', CartItems);
}
